import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';
import {
	domainUID,
	ExposedPromise,
	JClone,
	skillUID,
} from 'jakapa-utilities-lite';

export interface Domain {
	uid: domainUID;
	name: string;
	logo: string;
	key: string;
	skills: { [key in skillUID]?: Skill };
}

export interface Skill {
	uid: skillUID;
	name: string;
	domain: Domain;
}

/**
 * @deprecated
 */
export interface DepreciatedDomain {
	uid: number;
	name: string;
	skills: DepreciatedSkill[];
}

/**
 * @deprecated
 */
export interface DepreciatedSkill {
	uid: number;
	name: string;
	definition: string;
	description: string;
	questions: Array<string>;
}

const DOMAIN_AND_SKILL_UIDS: { [key in domainUID]: Array<skillUID> } = {
	1: [101, 102, 103, 104, 105, 106, 107, 108, 109],
	2: [201, 202, 203, 204, 205, 206],
	3: [301, 302, 303, 304, 305, 306, 307],
	4: [401, 402, 403, 404, 405],
	5: [501, 502, 503, 504, 505],
};
const DOMAIN_LOGOS: { [key in domainUID]: string } = {
	1: 'assets/img/self-management.svg',
	2: 'assets/img/innovation.svg',
	3: 'assets/img/emotional-resilience.svg',
	4: 'assets/img/social-engagement.svg',
	5: 'assets/img/cooperation.svg',
};
const DOMAIN_KEYS: { [key in domainUID]: string } = {
	1: 'self-management',
	2: 'innovation',
	3: 'emotional-resilience',
	4: 'social-engagement',
	5: 'cooperation',
};

@Injectable({
	providedIn: 'root',
})
export class DomainService {
	private _domains: { [key in domainUID]: Domain };
	private _skills: { [key in skillUID]: Skill };
	private _loaded: ExposedPromise<void> = new ExposedPromise<void>();

	constructor(private _languageSvc: LanguageService) {
		// Build domain and skill objects and retrieve name paths.
		this._domains = <{ [key in domainUID]: Domain }>{};
		this._skills = <{ [key in skillUID]: Skill }>{};
		const paths: Array<string> = [];
		for (const domainUID in DOMAIN_AND_SKILL_UIDS) {
			const domain: Domain = {
				uid: <domainUID>+domainUID,
				name: '',
				logo: DOMAIN_LOGOS[domainUID],
				key: DOMAIN_KEYS[domainUID],
				skills: {},
			};
			paths.push(`domains2.${domainUID}.name`);
			for (const skillUID of DOMAIN_AND_SKILL_UIDS[domainUID]) {
				const skill: Skill = { uid: skillUID, name: '', domain };
				domain.skills[skillUID] = skill;
				this._skills[skillUID] = skill;
				paths.push(`domains2.${domainUID}.skills.${skillUID}.name`);
			}
			this._domains[domainUID] = domain;
		}

		// Load domain and skill names.
		this._languageSvc.get(paths).then((value) => {
			for (const domain of Object.values(this._domains)) {
				const path: string = `domains2.${domain.uid}.name`;
				if (typeof value[path] === 'string') domain.name = value[path];
				for (const skill of Object.values(domain.skills)) {
					const path: string = `domains2.${domain.uid}.skills.${skill.uid}.name`;
					if (typeof value[path] === 'string') skill.name = value[path];
				}
			}
			this._loaded.resolve();
		});
	}

	/** Returns a promise that resolves once all names have been loaded in.
	 *
	 * @returns {Promise<void>}
	 */
	get loaded(): Promise<void> {
		return this._loaded.toPromise();
	}

	/** Returns the domain object with the matching UID.
	 *
	 * @param {domainUID | `${domainUID}`} uid The UID of the domain to retrieve.
	 * @returns {Domain}
	 */
	getDomain(uid: domainUID | `${domainUID}`): Domain {
		return JClone(this._domains[uid]);
	}

	/** Returns an array of all domain objects.
	 *
	 * @returns {Array<Domain>}
	 */
	getDomains(): Array<Domain> {
		return JClone(Object.values(this._domains));
	}

	/** Returns the skill object with the matching UID.
	 *
	 * @param {skillUID | `${skillUID}`} uid The UID of the skill to retrieve.
	 * @returns {Skill}
	 */
	getSkill(uid: skillUID | `${skillUID}`): Skill {
		return JClone(this._skills[uid]);
	}

	/** Returns an array of all skill objects.
	 *
	 * @param {domainUID | `${domainUID}` | undefined} domainUID
	 *  Optional. Filters the array by a specific domain UID.
	 * @returns {Array<Skill>}
	 */
	getSkills(domainUID?: domainUID | `${domainUID}`): Array<Skill> {
		if (!domainUID) return JClone(Object.values(this._skills));
		return JClone(Object.values(this._domains[domainUID].skills));
	}

	/** Returns the domain or skill object with the matching UID.
	 *
	 * @param {domainUID | skillUID | `${domainUID}` | `${skillUID}`} uid
	 * 	The UID of the domain or skill to retrieve.
	 * @returns {Domain | Skill}
	 */
	getDomainOrSkill(
		uid: domainUID | skillUID | `${domainUID}` | `${skillUID}`,
	): Domain | Skill {
		if (+uid < 100) return JClone(this._domains[uid]);
		return JClone(this._skills[uid]);
	}
}
